import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Fab,
  FormControlLabel,
  FormGroup,
  Grid,
  makeStyles,
  MenuItem,
  TextField,
  ThemeProvider,
  Typography,
  withStyles,
} from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'app';
import { useLocation } from 'react-router-dom';
import LoadingButton from 'common/components/buttons/LoadingButton';
import MessagingAPI from 'components/messaging/api/messaging-api';
import Video, { createLocalTracks, LocalTrackPublication, Participant, Room } from 'twilio-video';
import LocalParticipant from './LocalParticipant';
import RemoteParticipant from './RemoteParticipant';

import { CallEnd, Videocam, VideocamOff } from '@material-ui/icons';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import { createTheme } from '@mui/material/styles';
import Switch from '@mui/material/Switch';

const useStyles = makeStyles((theme) => ({
  grid: {
    height: '100%',
    textAlign: 'center',
  },
  root: {
    minWidth: 300,
    maxWidth: '90vw',
    maxHeight: '80vh',
    minHeight: 300,
  },
}));

enum VideoCallState {
  AboutToStart,
  Started,
  Ended,
}

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const VideoCall: React.FC = () => {
  const query = useQuery();
  const s: string = query.get('s') ?? '';
  const [disabled, setDisabled] = useState(false);
  const dispatch = useAppDispatch();
  const [callStatus, setCallStatus] = useState<VideoCallState>(VideoCallState.AboutToStart);
  const [loading, setLoading] = useState<boolean>(false);
  const [room, setRoom] = useState<Room | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [token, setToken] = useState('');

  const [enableVideo, setVideoEnable] = useState<boolean>(true);
  const [enableAudio, setAudioEnable] = useState<boolean>(true);

  const [audioTracks, setAudioTracks] = useState<MediaDeviceInfo[]>([]);
  const [videoTracks, setVideoTracks] = useState<MediaDeviceInfo[]>([]);
  const [videoTrackIndex, setVideoTrackIndex] = useState<number>(0);
  const skipInitialVideoSwitchRef = useRef<boolean>(true);
  const videoSwitchRequestRef = useRef<number>(0);

  const [selectedAudioTrack, setSelectedAudioTrack] = useState<string>('');
  const [selectedVideoTrack, setSelectedVideoTrack] = useState<string>('');

  const [Name, setName] = useState<string>('');
  const [userType, setUserType] = useState<string>('');

  // iOS detection for camera handling
  const isIos =
    (/iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
    !(window as any).MSStream;

  // Camera facing mode for iOS
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

  // Helper to build video constraint based on selected device or facing mode
  const getVideoConstraint = () => {
    if (selectedVideoTrack) {
      return { deviceId: { exact: selectedVideoTrack } };
    }
    return { facingMode: { exact: facingMode } };
  };

  // Handle camera switching for both iOS and Android
  const handleSwitchCamera = () => {
    
    if (isIos) {
      // iOS: toggle between front and back cameras
      const next = facingMode === 'user' ? 'environment' : 'user';
      setFacingMode(next);
      setSelectedVideoTrack('');
      return;
    }

    // Android: cycle through available devices
    if (videoTracks.length > 1) {
      const selectedIndex = videoTracks.findIndex((track) => track.deviceId === selectedVideoTrack);
      const currentIndex = selectedIndex >= 0 ? selectedIndex : videoTrackIndex % videoTracks.length;
      const nextIndex = (currentIndex + 1) % videoTracks.length;
      const nextDeviceId = videoTracks[nextIndex].deviceId;
      setVideoTrackIndex(nextIndex);
      setSelectedVideoTrack(nextDeviceId);
      return;
    }

    // Fallback: use facingMode if only one device but facingMode is supported
    const next = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(next);
    setSelectedVideoTrack('');
  };

  async function GetToken() {
    setLoading(true);
    let response = await MessagingAPI.getVideoCallToken(s);
    setToken(response.token);
    setName(response.name);
    setUserType(response.type);
    setLoading(false);
  }

  async function StartCall() {
    const participantConnected = (participant: Participant) => {
      setParticipants([participant]);
    };
    const participantDisconnected = (participant: Participant) => {
      setParticipants([]);
    };
    const videoConstraint: any = getVideoConstraint();
    const audioConstraint: any = selectedAudioTrack ? { deviceId: selectedAudioTrack } : true;

    let tracks = await createLocalTracks({
      audio: audioConstraint,
      video: videoConstraint,
    });

    // Refresh devices after media permission is granted so all cameras are detected.
    await refreshMediaDevices();

    Video.connect(token, { name: '', tracks: tracks }).then((room: Room) => {
      setRoom(room);
      if (!enableAudio) {
        room.localParticipant.audioTracks.forEach((x) => x.track.disable());
      }
      if (!enableVideo) {
        room.localParticipant.videoTracks.forEach((x) => x.track.disable());
      }
      room.on('participantConnected', participantConnected);
      room.on('participantDisconnected', participantDisconnected);
      room.participants.forEach(participantConnected);
    });
  }

  async function refreshMediaDevices() {
    if (!navigator.mediaDevices?.enumerateDevices) {
      return;
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const video = devices.filter((x) => x.kind === 'videoinput');
    const audio = devices.filter((x) => x.kind === 'audioinput');

    setAudioTracks(audio);
    setVideoTracks(video);

    setSelectedVideoTrack((prev) => {
      if (prev && video.some((track) => track.deviceId === prev)) {
        return prev;
      }
      return video[0]?.deviceId || '';
    });

    setSelectedAudioTrack((prev) => {
      if (prev && audio.some((track) => track.deviceId === prev)) {
        return prev;
      }
      return audio[0]?.deviceId || '';
    });

    const selectedVideoIndex = video.findIndex((track) => track.deviceId === selectedVideoTrack);
    if (selectedVideoIndex >= 0) {
      setVideoTrackIndex(selectedVideoIndex);
    } else {
      setVideoTrackIndex(0);
    }
  }

  useEffect(() => {
    if (callStatus === VideoCallState.AboutToStart) {
      GetToken();
    } else if (callStatus === VideoCallState.Started) {
      StartCall();
    } else {
      if (room) {
        room.localParticipant.tracks.forEach((track: LocalTrackPublication) => {
          track.unpublish();
        });
        room.disconnect();
      }
    }
  }, [callStatus]);

  useEffect(() => {
    refreshMediaDevices();

    if (!navigator.mediaDevices?.addEventListener) {
      return;
    }

    const handleDeviceChange = () => {
      refreshMediaDevices();
    };

    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
    };
  }, []);

  useEffect(() => {
    if (callStatus === VideoCallState.Started) {
      refreshMediaDevices();
    }
  }, [callStatus]);

  useEffect(() => {
    if (!room) {
      skipInitialVideoSwitchRef.current = true;
      videoSwitchRequestRef.current = 0;
    }
  }, [room]);

  useEffect(() => {
    if (videoTracks.length === 0) {
      setVideoTrackIndex(0);
      return;
    }

    const selectedIndex = videoTracks.findIndex((track) => track.deviceId === selectedVideoTrack);
    if (selectedIndex >= 0) {
      setVideoTrackIndex(selectedIndex);
      return;
    }

    if (videoTrackIndex >= videoTracks.length) {
      setVideoTrackIndex(0);
    }
  }, [videoTracks, selectedVideoTrack]);

  // Handle video track switching during active call
  useEffect(() => {
    if (!room) {
      return;
    }

    if (skipInitialVideoSwitchRef.current) {
      skipInitialVideoSwitchRef.current = false;
      return;
    }

    let isMounted = true;
    const requestId = ++videoSwitchRequestRef.current;

    (async () => {
      try {
        
        // Unpublish and stop existing video tracks
        const existingVideoTracks = Array.from(room.localParticipant.videoTracks.values());
        for (const publication of existingVideoTracks) {
          publication.unpublish();
          publication.track.stop();
        }

        // Create new track with updated constraint
        const videoConstraint: any = getVideoConstraint();
        
        const tracks = await createLocalTracks({ video: videoConstraint });
        if (!isMounted) return;
        if (requestId !== videoSwitchRequestRef.current) {
          tracks.forEach((track) => {
            if ('stop' in track) {
              (track as any).stop();
            }
          });
          return;
        }

        const newTrack = tracks[0];
        if (!newTrack) {
          return;
        }

        // Publish the new track
        await room.localParticipant.publishTrack(newTrack);

        // Handle enable/disable state
        if (!enableVideo && 'disable' in newTrack) {
          (newTrack as any).disable();
        } else if (enableVideo && 'enable' in newTrack) {
          (newTrack as any).enable();
        }
      } catch (error) {
        // If exact constraint fails on some browsers/devices, retry with non-exact facingMode.
        try {
          const fallbackVideoConstraint: any = selectedVideoTrack
            ? { deviceId: selectedVideoTrack }
            : { facingMode };
          const fallbackTracks = await createLocalTracks({ video: fallbackVideoConstraint });
          if (!isMounted) return;
          if (requestId !== videoSwitchRequestRef.current) {
            fallbackTracks.forEach((track) => {
              if ('stop' in track) {
                (track as any).stop();
              }
            });
            return;
          }

          room.localParticipant.videoTracks.forEach((publication) => {
            publication.unpublish();
            publication.track.stop();
          });

          const fallbackTrack = fallbackTracks[0];
          if (!fallbackTrack) {
            return;
          }

          await room.localParticipant.publishTrack(fallbackTrack);
          if (!enableVideo && 'disable' in fallbackTrack) {
            (fallbackTrack as any).disable();
          } else if (enableVideo && 'enable' in fallbackTrack) {
            (fallbackTrack as any).enable();
          }
        } catch (_fallbackError) {
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [selectedVideoTrack, facingMode, room, enableVideo]);

  // Handle audio track switching during active call
  useEffect(() => {
    if (!room || !selectedAudioTrack) {
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        
        // Unpublish and stop existing audio tracks
        const existingAudioTracks = Array.from(room.localParticipant.audioTracks.values());
        for (const publication of existingAudioTracks) {
          publication.unpublish();
          publication.track.stop();
        }

        // Create new audio track
        const tracks = await createLocalTracks({ audio: { deviceId: selectedAudioTrack } });
        if (!isMounted) return;
        
        const newTrack = tracks[0];
        if (!newTrack) {
          return;
        }

        // Publish the new track
        await room.localParticipant.publishTrack(newTrack);

        // Handle enable/disable state
        if (!enableAudio && 'disable' in newTrack) {
          (newTrack as any).disable();
        } else if (enableAudio && 'enable' in newTrack) {
          (newTrack as any).enable();
        }
      } catch (error) {
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [selectedAudioTrack, room, enableAudio]);

  // Handle enable/disable of video without replacing track
  useEffect(() => {
    if (!room) {
      return;
    }

    room.localParticipant.videoTracks.forEach((publication) => {
      if (enableVideo) {
        publication.track.enable();
      } else {
        publication.track.disable();
      }
    });
  }, [enableVideo, room]);

  // Handle enable/disable of audio without replacing track
  useEffect(() => {
    if (!room) {
      return;
    }

    room.localParticipant.audioTracks.forEach((publication) => {
      if (enableAudio) {
        publication.track.enable();
      } else {
        publication.track.disable();
      }
    });
  }, [enableAudio, room]);

  const handleDisconnect = () => {
    if (room) {
      room.localParticipant.tracks.forEach((track: LocalTrackPublication) => {
        track.unpublish();
      });
    }
    setCallStatus(VideoCallState.Ended);
  };

  const classes: any = useStyles();

  return (
    <ThemeProvider theme={darkTheme}>
      {/* ── PRE-CALL: device selection & join ── */}
      {callStatus === VideoCallState.AboutToStart && (
        <Grid container direction="column" justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
          <Card variant="outlined">
            <CardContent>
              {loading && <CircularProgress size={100} />}
              {!loading && (
                <div>
                  <Grid container>
                    <Grid item xs={12}>
                      <FormGroup row style={{ width: '100%' }}>
                        <TextField
                          style={{ width: '70%' }}
                          type="text"
                          variant="outlined"
                          name="username"
                          id="username"
                          value={Name}
                          disabled
                          label={userType.toLowerCase() === 'customer' ? 'Recipient' : 'Caller'}
                          onChange={(e) => setName(e.target.value)}
                          required
                        />
                        <div style={{ width: '5%' }}></div>
                        <Button
                          style={{ width: '25%', paddingTop: '0px', paddingBottom: '0px' }}
                          disableElevation
                          id="joinBtn"
                          color="primary"
                          disabled={disabled}
                          variant="contained"
                          type="submit"
                          onClick={() => setCallStatus(VideoCallState.Started)}>
                          Join Now
                        </Button>
                      </FormGroup>
                    </Grid>
                  </Grid>
                  <Grid container direction="row"><br /></Grid>
                  <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Grid item xs={4}>
                      <FormControlLabel
                        style={{ marginTop: '10px' }}
                        control={<Switch checked={enableAudio} />}
                        onClick={() => setAudioEnable(!enableAudio)}
                        color="primary"
                        label={
                          <Typography style={{ marginTop: '5px' }}>
                            {enableAudio ? <MicIcon /> : <MicOffIcon />}
                          </Typography>
                        }
                        labelPlacement="start"
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <TextField
                        select
                        variant="outlined"
                        margin="dense"
                        style={{ width: 200 }}
                        id="audioDevice"
                        name="audioDevice"
                        label="Audio Device"
                        value={selectedAudioTrack}
                        onChange={(e: any) => setSelectedAudioTrack(e.target.value)}>
                        {audioTracks.map((option: MediaDeviceInfo) => (
                          <MenuItem key={option.deviceId} value={option.deviceId}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                  <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Grid item xs={4}>
                      <FormControlLabel
                        style={{ marginTop: '10px' }}
                        control={<Switch checked={enableVideo} />}
                        onClick={() => setVideoEnable(!enableVideo)}
                        color="primary"
                        label={
                          <Typography style={{ marginTop: '5px' }}>
                            {enableVideo ? <Videocam /> : <VideocamOff />}
                          </Typography>
                        }
                        labelPlacement="start"
                      />
                    </Grid>
                    <Grid item xs={8}>
                      {isIos ? (
                        <FormControlLabel
                          control={<Switch checked={facingMode === 'environment'} />}
                          label={facingMode === 'environment' ? 'Back Camera' : 'Front Camera'}
                          onClick={() => {
                            const next = facingMode === 'user' ? 'environment' : 'user';
                            setFacingMode(next);
                            setSelectedVideoTrack('');
                          }}
                        />
                      ) : (
                        <TextField
                          select
                          variant="outlined"
                          margin="dense"
                          id="videoDevice"
                          name="videoDevice"
                          style={{ width: 200 }}
                          label="Video Device"
                          value={selectedVideoTrack}
                          onChange={(e: any) => setSelectedVideoTrack(e.target.value)}>
                          {videoTracks.map((option: MediaDeviceInfo) => (
                            <MenuItem key={option.deviceId} value={option.deviceId}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Grid>
                  </Grid>
                </div>
              )}
            </CardContent>
            <CardActions></CardActions>
          </Card>
        </Grid>
      )}

      {/* ── IN-CALL: full-screen remote + small PIP local + floating controls ── */}
      {callStatus === VideoCallState.Started && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: '#000',
            overflow: 'hidden',
          }}
        >
          {/* Remote participant – fills entire screen */}
          {room && participants[0] && (
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
              <RemoteParticipant participant={participants[0]} />
            </div>
          )}

          {/* Local participant – small PIP in bottom-right corner */}
          {room && (
            <LocalParticipant
              participant={room.localParticipant}
              videoTrackEnable={enableVideo}
              audioTrackEnable={enableAudio}
            />
          )}

          {/* Floating controls bar at the bottom center */}
          <div
            style={{
              position: 'fixed',
              bottom: 'max(12px, env(safe-area-inset-bottom))',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 16,
              zIndex: 2000,
            }}
          >
            <Fab
              size="medium"
              color={enableAudio ? 'primary' : 'secondary'}
              onClick={() => setAudioEnable(!enableAudio)}
              title="Mute">
              {enableAudio ? <MicIcon /> : <MicOffIcon />}
            </Fab>
            <Fab
              size="medium"
              color={enableVideo ? 'primary' : 'secondary'}
              onClick={() => setVideoEnable(!enableVideo)}
              title="Disable Video">
              {enableVideo ? <Videocam /> : <VideocamOff />}
            </Fab>
            <Fab
              size="medium"
              color="primary"
              onClick={handleSwitchCamera}
              style={{
                backgroundColor: '#f57c00',
                color: '#ffffff',
                border: '2px solid rgba(255,255,255,0.8)',
              }}
              aria-label="switch-camera"
              title="Switch Camera">
              <CameraswitchIcon />
            </Fab>
            <Fab
              size="medium"
              color="secondary"
              onClick={handleDisconnect}
              title="Disconnect">
              <CallEndIcon />
            </Fab>
          </div>
        </div>
      )}

      {/* ── POST-CALL: ended screen ── */}
      {callStatus === VideoCallState.Ended && (
        <Grid container justifyContent="center" alignItems="center" className={classes.grid}>
          <Grid item>
            <Box component="div" mt={1}>
              <Card className={classes.root}>
                <CardContent>
                  <Grid container direction="row" justifyContent="center" alignItems="center">
                    <Grid item xs={10}>
                      <Box mt={2}>
                        <Typography variant="h3" color="textSecondary" gutterBottom>
                          You've ended the call
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={10}>
                      <Box mt={2}>
                        <Typography variant="h4" color="textSecondary" gutterBottom>
                          You may close the window.
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box mt={2}>
                        <LoadingButton
                          fullWidth
                          id="login_btn"
                          color="primary"
                          disabled={disabled}
                          variant="contained"
                          size="medium"
                          type="submit"
                          onClick={() => setCallStatus(VideoCallState.Started)}>
                          Re-Join
                        </LoadingButton>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      )}
    </ThemeProvider>
  );
};

export default VideoCall;